import { useElevenLabsConversation } from './useElevenLabsConversation';
import { useGeminiConversation } from './useGeminiConversation';
import { _config } from '../Constants';
import { getUrlParameter } from '../utilities/urlUtils';

/**
 * Unified hook for realtime conversations
 * Automatically selects between ElevenLabs and Google Gemini based on configuration
 * Can be overridden with ?engine=gemini or ?engine=elevenlabs URL parameter
 *
 * Both engines route through Cloud Run WebSocket proxy for secure server-side prompt generation
 *
 * @param {Object} config - Configuration object (same API for both engines)
 * @returns {Object} Conversation controls and state
 */
export const useRealtimeConversation = (config) => {
  // Check URL parameter first, then fall back to config
  const urlEngine = getUrlParameter('engine');
  const engine = urlEngine || _config.realtimeEngine || 'elevenlabs';

  // Call both hooks unconditionally (React hooks rule)
  const elevenLabsConversation = useElevenLabsConversation(config);
  const geminiConversation = useGeminiConversation(config);

  // Return the appropriate one based on engine selection
  if (engine === 'gemini') {
    return geminiConversation;
  } else {
    // ElevenLabs (default)
    return elevenLabsConversation;
  }
};

/**
 * Helper to get the current realtime engine
 * Checks URL parameter first, then config
 * @returns {'elevenlabs' | 'gemini'}
 */
export const getRealtimeEngine = () => {
  const urlEngine = getUrlParameter('engine');
  return urlEngine || _config.realtimeEngine || 'elevenlabs';
};

/**
 * Helper to determine which endpoint to call for session setup
 * Checks URL parameter first, then config
 * @returns {string} The endpoint path
 */
export const getRealtimeEndpoint = () => {
  const urlEngine = getUrlParameter('engine');
  const engine = urlEngine || _config.realtimeEngine || 'elevenlabs';
  return engine === 'gemini' ? 'gemini-realtime' : 'agent';
};

/**
 * Helper to get custom model name from URL parameter
 * Allows quick testing of different models via ?model=<model_name>
 * Example: ?engine=gemini&model=gemini-3.8-live
 * @returns {string|null} Custom model name or null
 */
export const getCustomModel = () => {
  return getUrlParameter('model');
};

/**
 * Helper to get voice name from URL parameter
 * Allows quick testing of different voices via ?voice=<voice_name>
 * Example: ?engine=gemini&voice=Puck
 * Available voices: Puck, Charon, Kore, Fenrir, Aoede, Leda, Orus, Zephyr
 * Default: Zephyr
 * @returns {string} Voice name (defaults to 'Zephyr')
 */
export const getVoiceName = () => {
  return getUrlParameter('voice') || 'Zephyr';
};
